import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import withScreenshot from '../utils/withScreenshot';

const instances = new Map();
let observer;

if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const { isIntersecting } = entry;
            const instance = instances.get(entry.target);

            if (instance && isIntersecting && !instance.state.renderChildren) {
                if (instance.raf) {
                    cancelAnimationFrame(instance.raf);
                }

                instance.raf = requestAnimationFrame(() => {
                    if (instance.mounted) {
                        instance.setState({ renderChildren: true });
                        if (!instance.props.shouldUnmountOnViewportExit) {
                            Lazyload.unregisterInstance(instance);
                        }
                    }

                    instance.raf = null;
                });
            } else if (instance && !isIntersecting && instance.state.renderChildren) {
                if (instance.raf) {
                    cancelAnimationFrame(instance.raf);
                }

                instance.raf = requestAnimationFrame(() => {
                    if (instance.mounted) {
                        instance.setState({ renderChildren: false });
                    }

                    instance.raf = null;
                });
            }
        });
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
}

export class Lazyload extends PureComponent {
    static propTypes = {
        height: PropTypes.string, // can be px, %, etc.
        isScreenshotTest: PropTypes.bool,
        shouldUnmountOnViewportExit: PropTypes.bool,
        width: PropTypes.string, // can be px, %, etc.
    };

    static defaultProps = {
        shouldUnmountOnViewportExit: true,
    }

    static registerInstance = instance => {
        if (instances.size === 0 && !observer) {
            window.addEventListener('scroll', Lazyload.handleViewportChange, true);
            window.addEventListener('resize', Lazyload.handleViewportChange, true);
        }

        if (observer) {
            observer.observe(instance.$node);
        }

        instances.set(instance.$node, instance);

        if (!observer) {
            instance.handleViewportChange();
        }
    };

    static unregisterInstance = instance => {
        if (observer) {
            observer.unobserve(instance.$node);
        }

        instances.delete(instance.$node);

        if (instances.size === 0 && !observer) {
            window.removeEventListener('scroll', Lazyload.handleViewportChange, true);
            window.removeEventListener('resize', Lazyload.handleViewportChange, true);
        }
    };

    static handleViewportChange = () => instances.forEach(instance => instance.handleViewportChange());

    mounted = false

    state = {
        renderChildren: false,
    };

    componentDidMount() {
        this.mounted = true;
        Lazyload.registerInstance(this);
    }

    componentWillUnmount() {
        this.mounted = false;
        Lazyload.unregisterInstance(this);
    }

    render() {
        const {
            height,
            isScreenshotTest,
            shouldUnmountOnViewportExit,
            width,
            ...props
        } = this.props;

        return (
            <div
                {...props}
                ref={node => { this.$node = node; }}
                style={{
                    height,
                    width,
                }}
            >
                {this.maybeRenderChildren()}
            </div>
        );
    }

    maybeRenderChildren() {
        if (this.shouldRenderChildren()) {
            return this.props.children;
        }

        return ' '; // safari needs at least a space or the container will collapse
    }

    handleViewportChange() {
        const isVisible = this.isVisibleInViewport();
        if (isVisible) {
            this.setState({ renderChildren: true });
            if (!this.props.shouldUnmountOnViewportExit) {
                Lazyload.unregisterInstance(this);
            }
        } else if (this.props.shouldUnmountOnViewportExit) {
            this.setState({ renderChildren: false });
        }
    }

    shouldRenderChildren() {
        return this.state.renderChildren || this.props.isScreenshotTest || this.isVisibleInViewport();
    }

    isVisibleInViewport() {
        if (!this.$node) {
            return false;
        }

        const rect = this.$node.getBoundingClientRect();

        const height = rect.top - rect.bottom;
        if (height === 0) {
            return false;
        }

        const pageTop = 0;
        const pageRight = (window.innerWidth || document.documentElement.clientWidth);
        const pageBottom = (window.innerHeight || document.documentElement.clientHeight);
        const pageLeft = 0;

        return (
            rect.bottom >= pageTop &&
            rect.top <= pageBottom &&
            rect.right >= pageLeft &&
            rect.left <= pageRight
        );
    }
}

const ScreenshotAwareLazyload = withScreenshot(Lazyload);

export { ScreenshotAwareLazyload as default };
